import * as yup from 'yup'
import { createDataSchema } from '../schemaUtils'

const circleUnitType = [...(['px', 'em', 'rem', 'vh', 'vw', 'vmin', 'vmax'] as const)]
const unitType = [...([...circleUnitType, 'percentage'] as const)]

type UnitType = typeof unitType[number]
type CircleUnitType = typeof circleUnitType[number]

export const blendMode = [
	...([
		'normal',
		'multiply',
		'screen',
		'darken',
		'lighten',
		'overlay',
		'colorDodge',
		'colorBurn',
		'hardLight',
		'softLight',
		'difference',
		'exclusion',
		'hue',
		'saturation',
		'color',
		'luminosity',
	] as const),
]
export type BlendMode = typeof blendMode[number]

const GradientUnitSize = yup.object({
	value: yup.number(),
	type: yup.mixed<UnitType>().oneOf(unitType),
})
const CircleGradientUnitSize = yup.object({
	value: yup.number(),
	type: yup.mixed<CircleUnitType>().oneOf(circleUnitType),
})

const GradientColorStop = yup.object({
	size: GradientUnitSize.default(undefined),
	color: yup.string().default(undefined),
})

const unitAngleUnits = [...(['deg', 'percentage'] as const)]
type UnitAngle = typeof unitAngleUnits[number]

const GradientUnitAngle = yup.object({
	value: yup.string(),
	type: yup.mixed<UnitAngle>().oneOf(unitAngleUnits),
})

const GradientAngularColorStop = yup.object({
	size: GradientUnitAngle.default(undefined),
	color: yup.string().default(undefined),
})

const SolidColor = yup.object({
	type: yup.mixed<'SolidColor'>().oneOf(['SolidColor']),
	color: yup.string(),
})

const GradientLinear = yup.object({
	type: yup.mixed<'GradientLinear'>().oneOf(['GradientLinear']),
	repeat: yup.boolean(),
	blendMode: yup.mixed<BlendMode>().oneOf(blendMode),
	angle: yup.number().min(0).max(360),
	colorStops: yup.array().of(GradientColorStop),
})

const GradientCircle = yup.object({
	type: yup.mixed<'GradientCircle'>().oneOf(['GradientCircle']),
	repeat: yup.boolean(),
	blendMode: yup.mixed<BlendMode>().oneOf(blendMode),
	size: yup
		.object({
			keyword: yup
				.mixed()
				.oneOf(['closest-side', 'closest-corner', 'farthest-side', 'farthest-corner'])
				.default(undefined),
			value: yup.number().default(undefined),
			type: yup.mixed<CircleUnitType>().oneOf(circleUnitType).default(undefined),
		})
		.default(undefined),
	position: yup
		.object({
			x: CircleGradientUnitSize,
			y: CircleGradientUnitSize,
		})
		.default(undefined),
	colorStops: yup.array().of(GradientColorStop),
})

const GradientEllipse = yup.object({
	type: yup.mixed<'GradientEllipse'>().oneOf(['GradientEllipse']),
	repeat: yup.boolean(),
	blendMode: yup.mixed<BlendMode>().oneOf(blendMode),
	size: yup
		.object({
			keyword: yup
				.mixed()
				.oneOf(['closest-side', 'closest-corner', 'farthest-side', 'farthest-corner'])
				.default(undefined),
			width: GradientUnitSize.default(undefined),
			height: GradientUnitSize.default(undefined),
		})
		.default(undefined),
	position: yup
		.object({
			x: GradientUnitSize,
			y: GradientUnitSize,
		})
		.default(undefined),
	colorStops: yup.array().of(GradientColorStop),
})

const GradientConic = yup.object({
	type: yup.mixed<'GradientConic'>().oneOf(['GradientConic']),
	repeat: yup.boolean(),
	blendMode: yup.mixed<BlendMode>().oneOf(blendMode),
	angle: yup.number().min(0).max(360),
	position: yup
		.object({
			x: GradientUnitSize,
			y: GradientUnitSize,
		})
		.default(undefined),
	colorStops: yup.array().of(GradientAngularColorStop),
})

const GradientMesh = yup.object({
	type: yup.mixed<'GradientMesh'>().oneOf(['GradientMesh']),
	gradients: yup.lazy<
		| yup.InferType<typeof GradientCircle>
		| yup.InferType<typeof GradientEllipse>
		| yup.InferType<typeof GradientConic>
		| yup.InferType<typeof GradientLinear>
	>((obj: any) => {
		switch (obj.type) {
			case 'GradientCircle':
				return GradientCircle
			case 'GradientEllipse':
				return GradientEllipse
			case 'GradientConic':
				return GradientConic
			case 'GradientLinear':
			default:
				return GradientLinear
		}
	}),
})

export const ColorLayerSchema = createDataSchema(
	'ColorLayer',
	yup.object({
		opacity: yup.number().min(0).max(1).notRequired(),
		fill: yup.lazy<
			| yup.InferType<typeof SolidColor>
			| yup.InferType<typeof GradientCircle>
			| yup.InferType<typeof GradientEllipse>
			| yup.InferType<typeof GradientConic>
			| yup.InferType<typeof GradientLinear>
			| yup.InferType<typeof GradientMesh>
		>((obj: any) => {
			switch (obj.type) {
				case 'GradientLinear':
					return GradientLinear
				case 'GradientCircle':
					return GradientCircle
				case 'GradientEllipse':
					return GradientEllipse
				case 'GradientConic':
					return GradientConic
				case 'GradientMesh':
					return GradientMesh
				case 'SolidColor':
				default:
					return SolidColor
			}
		}),
	})
)
